/**
 * DeliveryPerformancePage — `/delivery/performance`.
 *
 * Live · GET /v1/delivery/performance (computed from route + stop + POD data
 * inside service-delivery; no external aggregator). Headline tiles are
 * operator-wide; the table shows per-driver rollups.
 */
import type { JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, ClipboardCheck, Clock, Users } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { getDeliveryPerformance } from '../../lib/api/delivery';
import { PageHeader } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

export function DeliveryPerformancePage(): JSX.Element {
  const performance = useQuery({
    queryKey: ['delivery', 'performance', 30],
    queryFn: () => getDeliveryPerformance(30),
    retry: false,
    staleTime: 60_000,
  });

  const denied =
    performance.isError &&
    performance.error instanceof ApiError &&
    (performance.error.status === 401 || performance.error.status === 403);
  const data = performance.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Delivery Performance"
        description="Per-driver and operator-wide on-time, POD acceptance and average time per stop."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Active drivers"
          value={data ? String(data.activeDrivers) : performance.isPending ? '…' : '—'}
          hint="status=ACTIVE"
          icon={Users}
        />
        <KPICard
          title="On-time delivery %"
          value={data ? `${data.onTimeRate}%` : '—'}
          hint="delivered / attempted"
          icon={ClipboardCheck}
          tone="success"
        />
        <KPICard
          title="Avg minutes / stop"
          value={data?.avgMinutesPerStop != null ? String(data.avgMinutesPerStop) : '—'}
          hint="completed - arrived"
          icon={Clock}
        />
        <KPICard
          title="POD acceptance %"
          value={data ? `${data.podAcceptanceRate}%` : '—'}
          hint="approved / reviewed"
          icon={BarChart3}
        />
      </div>

      {denied ? (
        <p className="text-sm text-amber-700">
          Permission required: <code className="font-mono">Delivery/readPerformance</code>
        </p>
      ) : null}

      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Per-driver performance</h2>
        {performance.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : performance.isError && !denied ? (
          <p className="text-sm text-red-700">{(performance.error as Error).message}</p>
        ) : (
          <div className="bg-white border border-border rounded-xl overflow-hidden" data-testid="performance-table">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Driver</th>
                  <th className="text-right px-4 py-2">Attempted</th>
                  <th className="text-right px-4 py-2">Delivered</th>
                  <th className="text-right px-4 py-2">On-time %</th>
                  <th className="text-right px-4 py-2">POD %</th>
                  <th className="text-right px-4 py-2">Avg min</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(data?.drivers ?? []).map((d) => (
                  <tr key={d.driverId} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-xs">
                      {d.fullName} <span className="text-muted ml-1">{d.driverNumber}</span>
                    </td>
                    <td className="px-4 py-2 text-right text-xs">{d.stopsAttempted}</td>
                    <td className="px-4 py-2 text-right text-xs">{d.stopsDelivered}</td>
                    <td className="px-4 py-2 text-right text-xs">
                      {d.onTimeRate != null ? `${d.onTimeRate}%` : '—'}
                    </td>
                    <td className="px-4 py-2 text-right text-xs">
                      {d.podAcceptanceRate != null ? `${d.podAcceptanceRate}%` : '—'}
                    </td>
                    <td className="px-4 py-2 text-right text-xs">
                      {d.avgMinutesPerStop != null ? d.avgMinutesPerStop : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(data?.drivers.length ?? 0) === 0 && !performance.isPending ? (
              <p className="p-8 text-center text-sm text-muted">No drivers in scope.</p>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
